const API_BASE = 'https://api.dev.auxo.dk'

/**
 * Optimizes an Auxo CDN image URL using Cloudflare on-the-fly resizing.
 */
export function getResizedImageUrl(url, { width = 800, quality = 80, height } = {}) {
  if (!url || typeof url !== 'string') return ''
  if (!url.startsWith('https://assets.auxodev.dk/')) return url
  if (url.includes('/cdn-cgi/image/')) return url

  const path = url.replace('https://assets.auxodev.dk/', '')
  const params = [`width=${width}`, `quality=${quality}`, 'format=auto']
  if (height) params.push(`height=${height}`)

  return `https://assets.auxodev.dk/cdn-cgi/image/${params.join(',')}/${path}`
}

/**
 * Fetches content from CMS collection using v2 endpoint
 */
export async function fetchCMSContent(collectionName, queryParams = {}) {
  try {
    const url = new URL(`${API_BASE}/public/v2/content/${collectionName}`)
    Object.entries(queryParams).forEach(([key, val]) => {
      if (val !== undefined && val !== null) {
        url.searchParams.append(key, String(val))
      }
    })

    const res = await fetch(url.toString())
    if (!res.ok) {
      console.warn(`[CMS] Failed to fetch ${collectionName}: ${res.status}`)
      return { items: [], total: 0 }
    }
    const data = await res.json()
    return data && Array.isArray(data.items) ? data : { items: [], total: 0 }
  } catch (err) {
    console.error(`[CMS] Error fetching ${collectionName}:`, err)
    return { items: [], total: 0 }
  }
}

/**
 * Fetches a gallery by its slug from CMS
 */
export async function fetchGalleryBySlug(gallerySlug) {
  try {
    const data = await fetchCMSContent('galleries', { limit: 50 })
    if (!data.items || data.items.length === 0) return null

    const match = data.items.find(item => {
      const slugVal = typeof item.slug === 'object' ? item.slug._ : item.slug
      return slugVal === gallerySlug
    })

    return match || null
  } catch (err) {
    console.error(`[CMS] Error fetching gallery "${gallerySlug}":`, err)
    return null
  }
}

/**
 * Fetches page content by slug
 */
export async function fetchPageContent(pageSlug) {
  try {
    const data = await fetchCMSContent('pages', { limit: 50 })
    if (!data.items || data.items.length === 0) return null

    const match = data.items.find(item => {
      const slugVal = typeof item.slug === 'object' ? item.slug._ : item.slug
      return slugVal === pageSlug
    })

    return match || null
  } catch (err) {
    console.error(`[CMS] Error fetching page "${pageSlug}":`, err)
    return null
  }
}

const fallbackVehicles = [
  {
    id: '48b1f75c-e023-4110-af37-ef1a8a943ff5',
    name: 'Kassevogn',
    pallet_capacity: '3-4 paller',
    temp_range: 'Temperaturen kan ikke justeres',
    payload: 'Kapacitet: 3-4 paller',
    category: 'Ekspres & Godstransport',
    features: 'Kapacitet: 3-4 paller. Temperaturen kan ikke justeres.',
    description: '<p>Kapacitet: 3-4 paller. Temperaturen kan ikke justeres.</p>',
    image: ['https://assets.auxodev.dk/assets/070d40dc-3ad7-4f6e-9a43-382bc6e79eb3/vehicle-kassevogn-photo-1.webp'],
    order: 1
  },
  {
    id: '314ac05e-04b3-4023-b16a-e75806630a94',
    name: 'Kølevogn',
    pallet_capacity: '3-4 paller',
    temp_range: '-20 °C til +25 °C',
    payload: 'Kapacitet: 3-4 paller',
    category: 'Temperaturstyret transport',
    features: 'Kapacitet: 3-4 paller. Temperaturområde: -20 °C til +25 °C.',
    description: '<p>Kapacitet: 3-4 paller. Temperaturområde: -20 °C til +25 °C.</p>',
    image: ['https://assets.auxodev.dk/assets/070d40dc-3ad7-4f6e-9a43-382bc6e79eb3/vehicle-koelevogn-photo-2.webp'],
    order: 2
  },
  {
    id: '95db85a6-e206-4576-8fe5-bae033f042c1',
    name: 'Kølevogn MAX',
    pallet_capacity: '4-5 paller',
    temp_range: '-20 °C til +25 °C',
    payload: 'Kapacitet: 4-5 paller',
    category: 'Temperaturstyret transport',
    features: 'Kapacitet: 4-5 paller. Temperaturområde: -20 °C til +25 °C.',
    description: '<p>Kapacitet: 4-5 paller. Temperaturområde: -20 °C til +25 °C.</p>',
    image: ['https://assets.auxodev.dk/assets/070d40dc-3ad7-4f6e-9a43-382bc6e79eb3/vehicle-koelevogn-max-photo-3.webp'],
    order: 3
  },
  {
    id: '5bd229e9-1e76-4188-9c89-f378680d0213',
    name: 'Kølevogn med lift',
    pallet_capacity: 'Op til 6 paller',
    temp_range: '-20 °C til +25 °C',
    payload: 'Kapacitet: op til 6 paller',
    category: 'Temperaturstyret transport',
    features: 'Kapacitet: op til 6 paller. Temperaturområde: -20 °C til +25 °C.',
    description: '<p>Kapacitet: op til 6 paller. Temperaturområde: -20 °C til +25 °C.</p>',
    image: ['https://assets.auxodev.dk/assets/070d40dc-3ad7-4f6e-9a43-382bc6e79eb3/vehicle-koelevogn-lift-6-paller-photo-4.webp'],
    order: 4
  },
  {
    id: '8f661690-eb2d-47a4-9be7-65d5d717e984',
    name: 'Kølevogn med lift',
    pallet_capacity: 'Op til 8 paller',
    temp_range: '-20 °C til +25 °C',
    payload: 'Kapacitet: op til 8 paller',
    category: 'Temperaturstyret transport',
    features: 'Kapacitet: op til 8 paller. Temperaturområde: -20 °C til +25 °C.',
    description: '<p>Kapacitet: op til 8 paller. Temperaturområde: -20 °C til +25 °C.</p>',
    image: ['https://assets.auxodev.dk/assets/070d40dc-3ad7-4f6e-9a43-382bc6e79eb3/vehicle-koelevogn-lift-8-paller-photo-5.webp'],
    order: 5
  }
]

/**
 * Fetches all vehicles from CMS sorted by order
 */
export async function fetchVehicles() {
  const data = await fetchCMSContent('vehicles', { sort: 'order', order: 'asc', limit: 20 })
  if (data.items && data.items.length > 0) {
    return data.items
  }
  return fallbackVehicles
}

/**
 * Fetches navigation items
 */
export async function fetchNavigation() {
  const data = await fetchCMSContent('navigation', { sort: 'order', order: 'asc', limit: 30 })
  return data.items || []
}
